/**
 * The Filters box's arithmetic, shared by every table that has one: the
 * Directory, a Team's members, a member's Teams, the company's Teams, and Boards.
 *
 * A table keeps two copies of its filters. The applied copy is what the listing
 * was fetched with; the draft is what the open box shows. Fields edit the draft
 * only: Apply commits it, Cancel throws it away, and Clear all empties it and
 * applies that at once (see FiltersPanelMixin). These helpers count, compare and
 * clean the copies, so every table does it the same way.
 *
 * A table describes its filters by their resting state: what the box holds when
 * nothing narrows the list. Most fields rest at null (a list field at []). A
 * field that always holds a value, such as a Team being Active or Deactivated,
 * rests at its default, and holding the default is not a filter.
 */

export type PanelFilterValue = string | string[] | boolean | null;
export type PanelFilters = Record<string, PanelFilterValue>;

function sameValue(a: PanelFilterValue, b: PanelFilterValue): boolean {
	if (Array.isArray(a) || Array.isArray(b)) {
		return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item: string, i: number) => item === b[i]);
	}
	return a === b;
}

/**
 * One field's value, cleaned against its resting value: text is trimmed (and
 * blank text rests), a list keeps its distinct non-blank strings in order, a
 * yes-or-no stays as it is, and anything else rests.
 */
function cleanValue(value: unknown, resting: PanelFilterValue): PanelFilterValue {
	if (Array.isArray(resting)) {
		if (!Array.isArray(value)) return [...resting];
		const items: string[] = value
			.filter((item: unknown): item is string => typeof item === 'string')
			.map((item: string) => item.trim())
			.filter((item: string) => !!item);
		return Array.from(new Set(items)).sort();
	}
	if (typeof value === 'string') {
		const trimmed: string = value.trim();
		return trimmed || resting;
	}
	if (typeof value === 'boolean') return value;
	return resting;
}

/**
 * A clean copy shaped like `resting`: exactly its keys, each cleaned. Use it to
 * seed a draft, to commit one, and to read filters back from the store, where
 * nothing is trusted.
 */
export function normalizePanelFilters<T extends PanelFilters>(
	resting: T,
	value?: Partial<Record<keyof T, unknown>> | null,
): T {
	const out: Partial<T> = {};
	Object.keys(resting).forEach((key: string) => {
		const candidate: unknown = value ? (value as Record<string, unknown>)[key] : undefined;
		(out as Record<string, PanelFilterValue>)[key] = cleanValue(candidate, resting[key]);
	});
	return out as T;
}

/** How many fields narrow the list: those not at their resting value. */
export function countPanelFilters<T extends PanelFilters>(
	resting: T,
	value?: Partial<Record<keyof T, unknown>> | null,
): number {
	const clean: T = normalizePanelFilters(resting, value);
	return Object.keys(resting).filter((key: string) => !sameValue(clean[key], resting[key])).length;
}

/** Whether two copies would fetch the same listing. */
export function samePanelFilters<T extends PanelFilters>(
	resting: T,
	a?: Partial<Record<keyof T, unknown>> | null,
	b?: Partial<Record<keyof T, unknown>> | null,
): boolean {
	const cleanA: T = normalizePanelFilters(resting, a);
	const cleanB: T = normalizePanelFilters(resting, b);
	return Object.keys(resting).every((key: string) => sameValue(cleanA[key], cleanB[key]));
}
